import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../../contexts/AuthContext';
import { getCreditBalance, getUsageAnalytics } from '../../services/creditService';
import { listApiKeys } from '../../services/apiKeyService';
// Assuming you have a spinner component, or you can use simple text
// import { Spinner } from '../../components/Spinner';

const DashboardPage: React.FC = () => {
  const { user } = useAuth();

  // Fetch Credit Balance
  const {
    data: creditBalanceData,
    isLoading: isLoadingCreditBalance,
    isError: isErrorCreditBalance
  } = useQuery({
    queryKey: ['creditBalance'],
    queryFn: getCreditBalance,
  });

  // Fetch API Keys
  const {
    data: apiKeysData,
    isLoading: isLoadingApiKeys,
    isError: isErrorApiKeys
  } = useQuery({
    queryKey: ['apiKeysList'],
    queryFn: listApiKeys,
  });

  // Fetch Usage Analytics
  const {
    data: usageAnalyticsData,
    isLoading: isLoadingUsageAnalytics,
    isError: isErrorUsageAnalytics
  } = useQuery({
    queryKey: ['usageAnalyticsOverview'],
    queryFn: () => getUsageAnalytics(undefined, undefined, 'day'),
  });

  return (
    <div>
      <div className="pb-5 border-b border-gray-200">
        <h1 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
          Welcome, {user?.email?.split('@')[0] || 'User'}!
        </h1>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {/* Credit Balance Card */}
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0 bg-primary-500 rounded-md p-3">
                <svg className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Credit Balance</dt>
                  <dd>
                    {isLoadingCreditBalance ? (
                      <div className="text-sm font-medium text-gray-700">Loading...</div>
                    ) : isErrorCreditBalance ? (
                      <div className="text-sm font-medium text-red-500">Error</div>
                    ) : (
                      <div className="text-lg font-medium text-gray-900">
                        {creditBalanceData?.credit_balance ?? 0} credits
                      </div>
                    )}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
          <div className="bg-gray-50 px-4 py-4 sm:px-6">
            <div className="text-sm">
              <a href="/dashboard/credits" className="font-medium text-primary-600 hover:text-primary-500">
                Add credits
                <span aria-hidden="true"> &rarr;</span>
              </a>
            </div>
          </div>
        </div>

        {/* API Keys Card */}
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0 bg-secondary-500 rounded-md p-3">
                <svg className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1721 9z" />
                </svg>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Active API Keys</dt>
                  <dd>
                    {isLoadingApiKeys ? (
                      <div className="text-sm font-medium text-gray-700">Loading...</div>
                    ) : isErrorApiKeys ? (
                      <div className="text-sm font-medium text-red-500">Error</div>
                    ) : (
                      <div className="text-lg font-medium text-gray-900">
                        {apiKeysData?.filter(key => key.is_active).length ?? 0} keys
                      </div>
                    )}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
          <div className="bg-gray-50 px-4 py-4 sm:px-6">
            <div className="text-sm">
              <a href="/dashboard/api-keys" className="font-medium text-primary-600 hover:text-primary-500">
                Manage API keys
                <span aria-hidden="true"> &rarr;</span>
              </a>
            </div>
          </div>
        </div>

        {/* Usage (Last 30 Days) Card */}
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0 bg-success-500 rounded-md p-3">
                <svg className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Usage (Last 30 Days)</dt>
                  <dd>
                    {isLoadingUsageAnalytics ? (
                      <div className="text-sm font-medium text-gray-700">Loading...</div>
                    ) : isErrorUsageAnalytics ? (
                      <div className="text-sm font-medium text-red-500">Error</div>
                    ) : (
                      <div className="text-lg font-medium text-gray-900">
                        {usageAnalyticsData?.summary_metrics.total_cost?.toFixed(2) ?? 0} credits
                      </div>
                    )}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
          <div className="bg-gray-50 px-4 py-4 sm:px-6">
            <div className="text-sm">
              <a href="/dashboard/analytics" className="font-medium text-primary-600 hover:text-primary-500">
                View all activity
                <span aria-hidden="true"> &rarr;</span>
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Activity Timeline */}
      <div className="mt-8">
        <h2 className="text-lg leading-6 font-medium text-gray-900">Recent Activity</h2>
        <div className="mt-2 bg-white shadow overflow-hidden sm:rounded-md">
          {isLoadingCreditBalance ? (
            <div className="px-4 py-4 sm:px-6 text-sm text-gray-500">Loading activity...</div>
          ) : isErrorCreditBalance ? (
            <div className="px-4 py-4 sm:px-6 text-sm text-red-500">Error loading activity</div>
          ) : creditBalanceData?.recent_transactions && creditBalanceData.recent_transactions.length > 0 ? (
            <ul className="divide-y divide-gray-200">
              {creditBalanceData.recent_transactions.slice(0, 5).map((transaction) => (
                <li key={transaction.id}>
                  <div className="px-4 py-4 sm:px-6">
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-medium text-primary-600 truncate">
                        {transaction.description}
                      </p>
                      <div className="ml-2 flex-shrink-0 flex">
                        <p className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          transaction.type === 'CREDIT' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}>
                          {transaction.amount} credits
                        </p>
                      </div>
                    </div>
                    <div className="mt-2 sm:flex sm:justify-between">
                      <div className="sm:flex">
                        <p className="flex items-center text-sm text-gray-500">
                          {new Date(transaction.timestamp).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <div className="px-4 py-4 sm:px-6">
              <p className="text-sm font-medium text-primary-600 truncate">No recent activity</p>
              <div className="mt-2 sm:flex sm:justify-between">
                <div className="sm:flex">
                  <p className="flex items-center text-sm text-gray-500">
                    Start using CrawlyCarl to see your activity here
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Usage Summary Text */}
      <div className="mt-8">
        <h2 className="text-lg leading-6 font-medium text-gray-900">Usage Summary</h2>
        <div className="mt-2 bg-white p-6 shadow rounded-lg">
          {isLoadingUsageAnalytics ? (
            <div className="text-sm font-medium text-gray-700">Loading usage summary...</div>
          ) : isErrorUsageAnalytics ? (
            <div className="text-sm font-medium text-red-500">Error loading usage summary</div>
          ) : (
            <p className="text-gray-700">
              Total usage in the last 30 days: {usageAnalyticsData?.summary_metrics.total_cost?.toFixed(2) ?? 0} credits.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;
